import React, { useState } from 'react';
import styled from 'styled-components';
import authService from '../../services/authService';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.overlayBackdrop};
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 24px;
  width: 100%;
  max-width: 420px;
  box-shadow: ${({ theme }) => theme.colors.shadowModal};
`;

const ModalTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.txtSecondary};
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${({ theme, $error }) => $error ? theme.colors.danger : theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.9rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
  }
`;

const ErrorMsg = styled.div`
  background: ${({ theme }) => theme.colors.dangerBg};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  color: ${({ theme }) => theme.colors.dangerTxt};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 8px 12px;
  font-size: 0.83rem;
  margin-bottom: 14px;
`;

const SuccessMsg = styled.div`
  background: ${({ theme }) => theme.colors.successBg};
  border: 1px solid ${({ theme }) => theme.colors.successBorder};
  color: ${({ theme }) => theme.colors.successTxt};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 14px;
  font-size: 0.88rem;
  text-align: center;
  margin-bottom: 16px;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
`;

const BtnPrimary = styled.button`
  flex: 1;
  padding: 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  min-height: 40px;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.88; }
`;

const BtnSecondary = styled.button`
  padding: 10px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  min-height: 40px;

  &:hover { background: ${({ theme }) => theme.colors.bgSecundario}; }
`;

export default function ModalCambiarPassword({ isOpen, onClose }) {
  const [form, setForm] = useState({ old_password: '', new_password: '', new_password_confirm: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleClose = () => {
    setForm({ old_password: '', new_password: '', new_password_confirm: '' });
    setError('');
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.old_password || !form.new_password || !form.new_password_confirm) {
      setError('Completá todos los campos.');
      return;
    }
    if (form.new_password !== form.new_password_confirm) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await authService.changePassword({
        old_password: form.old_password,
        new_password: form.new_password,
        new_password_confirm: form.new_password_confirm,
      });
      setSuccess(true);
    } catch (err) {
      const data = err.response?.data;
      if (data?.old_password) {
        setError(Array.isArray(data.old_password) ? data.old_password[0] : data.old_password);
      } else if (data?.new_password) {
        setError(Array.isArray(data.new_password) ? data.new_password[0] : data.new_password);
      } else if (data?.non_field_errors) {
        setError(Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors);
      } else if (typeof data === 'string') {
        setError(data);
      } else {
        setError('No se pudo cambiar la contraseña. Verificá los datos ingresados.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={buildOnBackdropClick(handleClose)} onMouseDown={onBackdropMouseDown}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalTitle>
          <i className="bi bi-key" />
          Cambiar contraseña
        </ModalTitle>

        {success ? (
          <>
            <SuccessMsg>
              <i className="bi bi-check-circle" style={{ marginRight: 6 }} />
              Contraseña cambiada exitosamente.
            </SuccessMsg>
            <BtnPrimary type="button" onClick={handleClose}>Cerrar</BtnPrimary>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <Field>
              <Label htmlFor="cp_old">Contraseña actual</Label>
              <Input
                id="cp_old"
                name="old_password"
                type="password"
                value={form.old_password}
                onChange={handleChange}
                autoComplete="current-password"
                $error={!!error && error.toLowerCase().includes('actual')}
              />
            </Field>
            <Field>
              <Label htmlFor="cp_new">Nueva contraseña</Label>
              <Input
                id="cp_new"
                name="new_password"
                type="password"
                value={form.new_password}
                onChange={handleChange}
                autoComplete="new-password"
              />
            </Field>
            <Field>
              <Label htmlFor="cp_confirm">Repetir nueva contraseña</Label>
              <Input
                id="cp_confirm"
                name="new_password_confirm"
                type="password"
                value={form.new_password_confirm}
                onChange={handleChange}
                autoComplete="new-password"
              />
            </Field>
            <BtnRow>
              <BtnSecondary type="button" onClick={handleClose}>Cancelar</BtnSecondary>
              <BtnPrimary type="submit" disabled={loading}>
                {loading ? 'Guardando...' : 'Cambiar contraseña'}
              </BtnPrimary>
            </BtnRow>
          </form>
        )}
      </Modal>
    </Overlay>
  );
}
